// スピン物理エンジン — 4 段階モデル (加速・巡航・減速・着地)
// 各段階で「終了秒・終了速度・曲線タイプ」を独立指定可能。
//
// 速度プロファイル:
//   v0=0 (固定) → v1 (① 終了) → v2 (② 終了) → v3 (③ 終了) → 0 (固定、④ 終了)
// 各段階は SpinCurve (linear / easeOut / easeIn / easeInOut) で形状を変えられる。

import type { MarkerPosition, Direction, SpinPhaseOverrides, SpinCurve, SpinPresetProfile } from './types';
import { pickWinnerWithSample, markerCanvasAngle } from './wheelLayout';
import type { WheelLayout } from './wheelLayout';

export type SpinPhase = 'idle' | 'spinning' | 'done';

export interface SpinCallbacks {
  onAngleUpdate: (angle: number) => void;
  onComplete: (winnerIndex: number, winnerText: string) => void;
  onTick?: () => void;
}

// ─── 4 段階モデルのプリセット定義 ──────────────────────────
// PRESETS は ratios で保持 (異なる duration 間補間用)。

interface PhaseRatios {
  push: number;
  cruise: number;
  decel: number;
  landing: number;
}

// プリセットプロファイル v5 (2026-04-28): バランス型 / 緩急強調 / AIお勧め の 3 種。
//   内部キーは 'x' / 'y' / 'z' のままだが意味を再定義:
//     z = バランス型 (本命): 7-12 秒はユーザー検証済の値、1-5 秒は初速早く + 停止ゆるやか
//     y = 緩急強調: 投擲 → 一気に止まりかけ → 長い余韻 (低 v3, 高 peak, 着地 65%+)
//     x = AIお勧め: 期待感クレッシェンド + 滑らかな減速 + 長い落下感 (decel も easeOut)
interface PresetEntry {
  durationMs: number;
  ratios: PhaseRatios;
  peakRps: number;
  vLowRps: number;
  pushCurve: SpinCurve;
  cruiseCurve: SpinCurve;
  decelCurve: SpinCurve;
  landingCurve: SpinCurve;
}

// z = バランス型 (本命) ★ 既定
const PROFILE_Z: PresetEntry[] = [
  { durationMs: 1000,  ratios: { push: 0.05, cruise: 0.15, decel: 0.25, landing: 0.55 }, peakRps: 15, vLowRps: 0.5,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 3000,  ratios: { push: 0.10, cruise: 0.12, decel: 0.25, landing: 0.53 }, peakRps: 8,  vLowRps: 0.5,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 5000,  ratios: { push: 0.15, cruise: 0.12, decel: 0.23, landing: 0.50 }, peakRps: 6,  vLowRps: 0.5,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 7000,  ratios: { push: 0.22, cruise: 0.11, decel: 0.22, landing: 0.45 }, peakRps: 5,  vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 9000,  ratios: { push: 0.22, cruise: 0.11, decel: 0.22, landing: 0.45 }, peakRps: 4,  vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 12000, ratios: { push: 0.22, cruise: 0.08, decel: 0.22, landing: 0.48 }, peakRps: 4,  vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
];

// y = 緩急強調: コントラスト最大化 (投擲感 + 長い余韻)
const PROFILE_Y: PresetEntry[] = [
  { durationMs: 1000,  ratios: { push: 0.03, cruise: 0.10, decel: 0.20, landing: 0.67 }, peakRps: 20, vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 3000,  ratios: { push: 0.05, cruise: 0.08, decel: 0.20, landing: 0.67 }, peakRps: 16, vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 5000,  ratios: { push: 0.07, cruise: 0.07, decel: 0.20, landing: 0.66 }, peakRps: 12, vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 7000,  ratios: { push: 0.10, cruise: 0.05, decel: 0.20, landing: 0.65 }, peakRps: 10, vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 9000,  ratios: { push: 0.12, cruise: 0.05, decel: 0.18, landing: 0.65 }, peakRps: 8,  vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
  { durationMs: 12000, ratios: { push: 0.12, cruise: 0.03, decel: 0.18, landing: 0.67 }, peakRps: 7,  vLowRps: 0.3,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'linear', landingCurve: 'easeOut' },
];

// x = AIお勧め: 滑らかなクレッシェンド + 滑らかな減速
const PROFILE_X: PresetEntry[] = [
  { durationMs: 1000,  ratios: { push: 0.08, cruise: 0.10, decel: 0.25, landing: 0.57 }, peakRps: 12,  vLowRps: 0.5,
    pushCurve: 'easeOut', cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
  { durationMs: 3000,  ratios: { push: 0.18, cruise: 0.08, decel: 0.22, landing: 0.52 }, peakRps: 8,   vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
  { durationMs: 5000,  ratios: { push: 0.22, cruise: 0.08, decel: 0.22, landing: 0.48 }, peakRps: 6,   vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
  { durationMs: 7000,  ratios: { push: 0.25, cruise: 0.07, decel: 0.22, landing: 0.46 }, peakRps: 5,   vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
  { durationMs: 9000,  ratios: { push: 0.28, cruise: 0.07, decel: 0.22, landing: 0.43 }, peakRps: 4.5, vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
  { durationMs: 12000, ratios: { push: 0.28, cruise: 0.05, decel: 0.22, landing: 0.45 }, peakRps: 4,   vLowRps: 0.5,
    pushCurve: 'easeIn',  cruiseCurve: 'linear', decelCurve: 'easeOut', landingCurve: 'easeOut' },
];

const PROFILES: Record<SpinPresetProfile, PresetEntry[]> = {
  x: PROFILE_X,
  y: PROFILE_Y,
  z: PROFILE_Z,
};

/** ピーク速度の許容範囲 (RPS)。デフォルトはプロファイルから動的取得。
 *  PEAK_SPEED_DEFAULT_RPS は UI のフォールバック (プロファイル未指定時) のみで使用。 */
export const PEAK_SPEED_DEFAULT_RPS = 4;
export const PEAK_SPEED_MIN_RPS = 0;
export const PEAK_SPEED_MAX_RPS = 25;

/** 曲線曲率指数 (ease 系で使用)。 */
const CURVE_POWER = 2;

/** 各 phase 境界の最小長 (ms)。極端に短くなりすぎないよう保証。 */
const MIN_PHASE_LEN_MS = 30;

/** 終了時間ランダム化の最大割合 (0-1)。 */
export const SPIN_DURATION_RANDOM_MAX_RATIO = 0.5;

/** スピン詳細ランダム化の最大強度 (0-1)。 */
export const SPIN_PHASE_RANDOMIZE_MAX = 1;

/** プリセットの基準 duration (ランダム抽選の候補)。 */
export const PRESET_DURATIONS_MS = [1000, 3000, 5000, 7000, 9000, 12000];

/** プリセットプロファイルの候補。 */
export const PRESET_PROFILES_LIST: SpinPresetProfile[] = ['x', 'y', 'z'];

/** ランダム抽選: 配列から均等選択。 */
function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// v5: DEFAULT_CURVES は廃止。各 PresetEntry が独自の curves を保持する。

/** 4 段階の絶対時間 + 速度 + 曲線を保持する内部構造。 */
export interface PhaseTimes {
  pushEndMs: number;
  cruiseEndMs: number;
  decelEndMs: number;
  totalMs: number;
  /** ① 加速の終端速度 (= ② 巡航の開始速度) */
  v1: number;
  /** ② 巡航の終端速度 (= ③ 減速の開始速度) */
  v2: number;
  /** ③ 減速の終端速度 (= ④ 着地の開始速度) */
  v3: number;
  pushCurve: SpinCurve;
  cruiseCurve: SpinCurve;
  decelCurve: SpinCurve;
  landingCurve: SpinCurve;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** プロファイル + duration から PresetEntry を補間取得。 */
function getPresetEntry(profile: SpinPresetProfile, durationMs: number): PresetEntry {
  const list = PROFILES[profile] ?? PROFILE_Z;
  const min = list[0];
  const max = list[list.length - 1];
  if (durationMs <= min.durationMs) return { ...min };
  if (durationMs >= max.durationMs) return { ...max };
  for (let i = 0; i < list.length - 1; i++) {
    const a = list[i];
    const b = list[i + 1];
    if (durationMs >= a.durationMs && durationMs <= b.durationMs) {
      const t = (durationMs - a.durationMs) / (b.durationMs - a.durationMs);
      // 補間: 数値は線形補間、curves は近い方の entry を採用 (中間 t<0.5 で a, t>=0.5 で b)
      const useA = t < 0.5;
      const c = useA ? a : b;
      return {
        durationMs,
        ratios: {
          push:    a.ratios.push    + (b.ratios.push    - a.ratios.push)    * t,
          cruise:  a.ratios.cruise  + (b.ratios.cruise  - a.ratios.cruise)  * t,
          decel:   a.ratios.decel   + (b.ratios.decel   - a.ratios.decel)   * t,
          landing: a.ratios.landing + (b.ratios.landing - a.ratios.landing) * t,
        },
        peakRps: a.peakRps + (b.peakRps - a.peakRps) * t,
        vLowRps: a.vLowRps + (b.vLowRps - a.vLowRps) * t,
        pushCurve: c.pushCurve,
        cruiseCurve: c.cruiseCurve,
        decelCurve: c.decelCurve,
        landingCurve: c.landingCurve,
      };
    }
  }
  return { ...max };
}

/** プリセット既定の PhaseTimes を返す (UI のリセット参照用にも使用)。 */
export function getPresetPhaseTimes(
  durationMs: number,
  profile: SpinPresetProfile = 'z',
): PhaseTimes {
  const e = getPresetEntry(profile, durationMs);
  return {
    pushEndMs:   durationMs * e.ratios.push,
    cruiseEndMs: durationMs * (e.ratios.push + e.ratios.cruise),
    decelEndMs:  durationMs * (e.ratios.push + e.ratios.cruise + e.ratios.decel),
    totalMs:     durationMs,
    v1:          e.peakRps,
    v2:          e.peakRps,                              // 巡航は定速 (= v1) を既定に
    v3:          e.vLowRps,
    pushCurve:    e.pushCurve,
    cruiseCurve:  e.cruiseCurve,
    decelCurve:   e.decelCurve,
    landingCurve: e.landingCurve,
  };
}

/** プリセット既定値に詳細設定 overrides を適用 + 安全クランプ。 */
export function getEffectivePhaseTimes(
  durationMs: number,
  overrides: SpinPhaseOverrides | null | undefined,
  profile: SpinPresetProfile = 'z',
): PhaseTimes {
  const preset = getPresetPhaseTimes(durationMs, profile);

  // 速度: 上書きされていなければ preset、ただし v2 のデフォルトは v1 (定速cruise)
  const v1 = clamp(overrides?.pushEndRps ?? preset.v1, PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);
  const v2 = clamp(overrides?.cruiseEndRps ?? v1, PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);
  const v3 = clamp(overrides?.decelEndRps ?? preset.v3, PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);

  // 時間境界
  const pushEnd = overrides?.pushEndMs ?? preset.pushEndMs;
  const cruiseEnd = overrides?.cruiseEndMs ?? preset.cruiseEndMs;
  const decelEnd = overrides?.decelEndMs ?? preset.decelEndMs;

  const safePushEnd = clamp(pushEnd, MIN_PHASE_LEN_MS, durationMs - 3 * MIN_PHASE_LEN_MS);
  const safeCruiseEnd = clamp(cruiseEnd, safePushEnd + MIN_PHASE_LEN_MS, durationMs - 2 * MIN_PHASE_LEN_MS);
  const safeDecelEnd = clamp(decelEnd, safeCruiseEnd + MIN_PHASE_LEN_MS, durationMs - MIN_PHASE_LEN_MS);

  return {
    pushEndMs: safePushEnd,
    cruiseEndMs: safeCruiseEnd,
    decelEndMs: safeDecelEnd,
    totalMs: durationMs,
    v1, v2, v3,
    pushCurve:    overrides?.pushCurve    ?? preset.pushCurve,
    cruiseCurve:  overrides?.cruiseCurve  ?? preset.cruiseCurve,
    decelCurve:   overrides?.decelCurve   ?? preset.decelCurve,
    landingCurve: overrides?.landingCurve ?? preset.landingCurve,
  };
}

/** スピン詳細ランダム化: 各 phase の長さと速度を ±intensity × 基準値 で揺らす。
 *  - 総時間 (totalMs) は維持
 *  - 各 phase 長さを摂動した後、合計が totalMs になるよう正規化
 *  - 速度 (v1/v2/v3) も独立に摂動 (PEAK_SPEED 範囲内にクランプ)
 *  - 最低 phase 長 50ms を確保 (ゼロ除算/極端値回避)
 */
function applyPhaseRandomizeToTimes(times: PhaseTimes, intensity: number): PhaseTimes {
  if (intensity <= 0) return times;
  const i = Math.min(SPIN_PHASE_RANDOMIZE_MAX, Math.max(0, intensity));
  const rand = () => 1 + (Math.random() * 2 - 1) * i;

  const pushLen = times.pushEndMs;
  const cruiseLen = times.cruiseEndMs - times.pushEndMs;
  const decelLen = times.decelEndMs - times.cruiseEndMs;
  const landingLen = times.totalMs - times.decelEndMs;

  // 各 phase 長を摂動 (最低 50ms 確保)
  let p1 = Math.max(50, pushLen * rand());
  let p2 = Math.max(50, cruiseLen * rand());
  let p3 = Math.max(50, decelLen * rand());
  let p4 = Math.max(50, landingLen * rand());

  // 合計が totalMs になるよう正規化
  const sum = p1 + p2 + p3 + p4;
  const scale = times.totalMs / sum;
  p1 *= scale; p2 *= scale; p3 *= scale; p4 *= scale;

  const newPushEnd = p1;
  const newCruiseEnd = p1 + p2;
  const newDecelEnd = p1 + p2 + p3;

  // 速度を摂動 + クランプ
  const newV1 = clamp(times.v1 * rand(), PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);
  const newV2 = clamp(times.v2 * rand(), PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);
  const newV3 = clamp(times.v3 * rand(), PEAK_SPEED_MIN_RPS, PEAK_SPEED_MAX_RPS);

  return {
    pushEndMs: newPushEnd,
    cruiseEndMs: newCruiseEnd,
    decelEndMs: newDecelEnd,
    totalMs: times.totalMs,
    v1: newV1,
    v2: newV2,
    v3: newV3,
    pushCurve: times.pushCurve,
    cruiseCurve: times.cruiseCurve,
    decelCurve: times.decelCurve,
    landingCurve: times.landingCurve,
  };
}

/** 終了時間ランダム化を PhaseTimes に適用。
 *  - delta < 0 (短縮): 巡航 → 着地 の順に削る
 *  - delta > 0 (延長): 着地に追加
 *  ratio = 0-1, 実 delta = ±(totalMs × ratio) のうちランダム
 */
function applyRandomDurationToTimes(times: PhaseTimes, ratio: number): PhaseTimes {
  if (ratio <= 0) return times;
  const r = Math.min(SPIN_DURATION_RANDOM_MAX_RATIO, Math.max(0, ratio));
  const deltaMs = (Math.random() * 2 - 1) * r * times.totalMs;
  if (deltaMs === 0) return times;

  const pushLen = times.pushEndMs;
  const cruiseLen = times.cruiseEndMs - times.pushEndMs;
  const decelLen = times.decelEndMs - times.cruiseEndMs;
  const landingLen = times.totalMs - times.decelEndMs;

  let newCruiseLen = cruiseLen;
  let newLandingLen = landingLen;

  if (deltaMs < 0) {
    let cut = -deltaMs;
    const cruiseAvail = Math.max(0, cruiseLen - 50);
    const cruiseCut = Math.min(cut, cruiseAvail);
    newCruiseLen = cruiseLen - cruiseCut;
    cut -= cruiseCut;
    if (cut > 0) {
      const landingAvail = Math.max(0, landingLen - 200);
      const landingCut = Math.min(cut, landingAvail);
      newLandingLen = landingLen - landingCut;
    }
  } else {
    newLandingLen = landingLen + deltaMs;
  }

  const newCruiseEnd = pushLen + newCruiseLen;
  const newDecelEnd = newCruiseEnd + decelLen;
  const newTotalMs = newDecelEnd + newLandingLen;

  return {
    ...times,
    cruiseEndMs: newCruiseEnd,
    decelEndMs: newDecelEnd,
    totalMs: newTotalMs,
  };
}

// ─── 速度カーブ計算 ─────────────────────────────────────────

/** 1 段階の累積角度を計算 (rotation 単位)。
 *  vStart, vEnd は RPS、phaseLengthSec は秒、localT は [0,1] の進行度。
 *
 *  速度プロファイル v(s) (s = localT):
 *    - linear:    vStart + (vEnd - vStart) × s
 *    - easeIn:    vStart + (vEnd - vStart) × s^p
 *    - easeOut:   vEnd - (vEnd - vStart) × (1-s)^p     (※ vEnd + (vStart - vEnd)(1-s)^p)
 *    - easeInOut: smoothstep
 *  各積分: ∫₀^s v(u) du, 結果に phaseLengthSec を掛けて回転数。
 */
function phaseAreaUpTo(
  localT: number,
  phaseLengthSec: number,
  vStart: number,
  vEnd: number,
  curve: SpinCurve,
): number {
  if (localT <= 0 || phaseLengthSec <= 0) return 0;
  const t = Math.min(1, localT);
  const dv = vEnd - vStart;
  const p = CURVE_POWER;
  let integral: number;

  switch (curve) {
    case 'linear':
      // v(s) = vStart + dv*s
      // ∫₀^t v ds = vStart*t + dv*t²/2
      integral = vStart * t + (dv * t * t) / 2;
      break;
    case 'easeIn':
      // v(s) = vStart + dv*s^p
      // ∫₀^t v ds = vStart*t + dv*t^(p+1)/(p+1)
      integral = vStart * t + (dv * Math.pow(t, p + 1)) / (p + 1);
      break;
    case 'easeOut':
      // v(s) = vEnd - dv*(1-s)^p   (= vEnd + (vStart-vEnd)(1-s)^p)
      // ∫₀^t v ds = vEnd*t - dv*(1 - (1-t)^(p+1))/(p+1)
      integral = vEnd * t - (dv * (1 - Math.pow(1 - t, p + 1))) / (p + 1);
      break;
    case 'easeInOut': {
      // S字 (smoothstep): v(s) = vStart + dv * smooth(s) where smooth = 3s²-2s³ (p=2)
      // 積分: ∫₀^t (3s²-2s³) ds = s³ - s⁴/2
      const sm = t * t * t - (t * t * t * t) / 2;
      integral = vStart * t + dv * sm;
      break;
    }
  }
  return phaseLengthSec * integral;
}

/** 速度プロファイル時刻 tMs における回転数 (rotation) を返す。 */
export function rotationsAt(tMs: number, times: PhaseTimes): number {
  if (tMs <= 0) return 0;

  const v0 = 0;
  const v1 = times.v1;
  const v2 = times.v2;
  const v3 = times.v3;
  const v4 = 0;

  const pushLenSec = times.pushEndMs / 1000;
  const cruiseLenSec = (times.cruiseEndMs - times.pushEndMs) / 1000;
  const decelLenSec = (times.decelEndMs - times.cruiseEndMs) / 1000;
  const landingLenSec = (times.totalMs - times.decelEndMs) / 1000;

  // ① push
  if (tMs <= times.pushEndMs) {
    return phaseAreaUpTo(tMs / times.pushEndMs, pushLenSec, v0, v1, times.pushCurve);
  }
  let r = phaseAreaUpTo(1, pushLenSec, v0, v1, times.pushCurve);

  // ② cruise
  if (tMs <= times.cruiseEndMs) {
    const localT = (tMs - times.pushEndMs) / (times.cruiseEndMs - times.pushEndMs);
    return r + phaseAreaUpTo(localT, cruiseLenSec, v1, v2, times.cruiseCurve);
  }
  r += phaseAreaUpTo(1, cruiseLenSec, v1, v2, times.cruiseCurve);

  // ③ decel
  if (tMs <= times.decelEndMs) {
    const localT = (tMs - times.cruiseEndMs) / (times.decelEndMs - times.cruiseEndMs);
    return r + phaseAreaUpTo(localT, decelLenSec, v2, v3, times.decelCurve);
  }
  r += phaseAreaUpTo(1, decelLenSec, v2, v3, times.decelCurve);

  // ④ landing
  if (tMs <= times.totalMs) {
    const localT = (tMs - times.decelEndMs) / (times.totalMs - times.decelEndMs);
    return r + phaseAreaUpTo(localT, landingLenSec, v3, v4, times.landingCurve);
  }
  return r + phaseAreaUpTo(1, landingLenSec, v3, v4, times.landingCurve);
}

// ─── スピン状態 ─────────────────────────────────────────────

interface SpinParams {
  startAngle: number;
  endAngle: number;
  startTime: number;
  times: PhaseTimes;
  /** 自然総角度 (rotationsAt(totalMs) * 2π) と endAngle の比率 */
  angleScale: number;
  spinDir: 1 | -1;
  winnerIndex: number;
  winnerText: string;
  rafId: number;
  segmentCount: number;
  prevAngle: number;
}

export class SpinEngine {
  private _angle = 0;
  private _phase: SpinPhase = 'idle';
  private sp: SpinParams | null = null;
  private readonly cb: SpinCallbacks;
  private oneShot: ((winnerIndex: number, winnerText: string) => void) | null = null;

  constructor(callbacks: SpinCallbacks) {
    this.cb = callbacks;
  }

  setOneShotComplete(cb: (winnerIndex: number, winnerText: string) => void): void {
    this.oneShot = cb;
  }

  get angle(): number { return this._angle; }
  get phase(): SpinPhase { return this._phase; }

  start(
    layout: WheelLayout,
    markerPos: MarkerPosition,
    markerDeg: number | undefined,
    durationMs: number,
    segmentDirection: Direction = 'cw',
    spinDirection: Direction = 'cw',
    options: {
      durationRandomRatio?: number;
      phaseOverrides?: SpinPhaseOverrides | null;
      presetProfile?: SpinPresetProfile;
      phaseRandomize?: number;
      presetRandom?: boolean;
      durationRandom?: boolean;
    } = {},
  ): { winnerItemIdx: number; totalMs: number } | false {
    if (this._phase !== 'idle') return false;
    if (layout.segments.length === 0) return false;

    const U = Math.random();
    const winnerIdx = pickWinnerWithSample(layout.segments, U);
    const winner = layout.segments[winnerIdx];
    const M = markerCanvasAngle(markerPos, markerDeg);

    const segSize = winner.endAngle - winner.startAngle;
    const segOffset = winner.startAngle + segSize * Math.random();
    const segDir = segmentDirection === 'ccw' ? -1 : 1;
    const base = M + Math.PI / 2 - segDir * segOffset;
    const start = this._angle;

    const spinDir: 1 | -1 = spinDirection === 'ccw' ? -1 : 1;
    let diff: number;
    if (spinDir === 1) {
      diff = ((base - start) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    } else {
      diff = ((start - base) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
    }
    if (diff < Math.PI * 0.05) diff += Math.PI * 2;

    // プリセット・秒数のスピン毎ランダム抽選 (各々独立)
    const effProfile = options.presetRandom
      ? pickRandom(PRESET_PROFILES_LIST)
      : (options.presetProfile ?? 'z');
    const effDurationMs = options.durationRandom
      ? pickRandom(PRESET_DURATIONS_MS)
      : durationMs;

    const baseTimes = getEffectivePhaseTimes(effDurationMs, options.phaseOverrides, effProfile);
    const durRandomized = applyRandomDurationToTimes(baseTimes, options.durationRandomRatio ?? 0);
    const times = applyPhaseRandomizeToTimes(durRandomized, options.phaseRandomize ?? 0);

    const naturalRotations = rotationsAt(times.totalMs, times);
    const naturalTotalRad = naturalRotations * Math.PI * 2;

    const N = Math.max(1, Math.round((naturalTotalRad - diff) / (Math.PI * 2)));
    const totalAngle = N * Math.PI * 2 + diff;
    const angleScale = naturalTotalRad > 0 ? totalAngle / naturalTotalRad : 1;
    const end = start + spinDir * totalAngle;

    const now = performance.now();
    this.sp = {
      startAngle: start,
      endAngle: end,
      startTime: now,
      times,
      angleScale,
      spinDir,
      winnerIndex: winnerIdx,
      winnerText: winner.text,
      rafId: requestAnimationFrame((t) => this.frame(t)),
      segmentCount: layout.segments.length,
      prevAngle: start,
    };
    this._phase = 'spinning';
    void U;
    return { winnerItemIdx: winner.itemIndex, totalMs: times.totalMs };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tap(_doubleWindowMs: number, _tripleWindowMs: number): void {
    // P1 では無効
  }

  jumpToEnd(): void {
    if (!this.sp) return;
    cancelAnimationFrame(this.sp.rafId);
    this._angle = this.sp.endAngle;
    this.cb.onAngleUpdate(this._angle);
    this.finish();
  }

  private frame(now: number): void {
    if (!this.sp) return;
    const { startTime, startAngle, times, angleScale, spinDir, segmentCount, prevAngle } = this.sp;
    const elapsed = now - startTime;
    const tMs = Math.min(elapsed, times.totalMs);
    const naturalAngle = rotationsAt(tMs, times) * Math.PI * 2;
    this._angle = startAngle + spinDir * (naturalAngle * angleScale);
    this.cb.onAngleUpdate(this._angle);

    if (this.cb.onTick && segmentCount > 0) {
      const segSize = (Math.PI * 2) / segmentCount;
      const prevTicks = Math.floor(prevAngle / segSize);
      const curTicks = Math.floor(this._angle / segSize);
      const delta = Math.abs(curTicks - prevTicks);
      const ticks = Math.min(5, delta);
      for (let i = 0; i < ticks; i++) this.cb.onTick();
    }
    this.sp.prevAngle = this._angle;

    if (elapsed >= times.totalMs) {
      this._angle = this.sp.endAngle;
      this.cb.onAngleUpdate(this._angle);
      this.finish();
    } else {
      this.sp.rafId = requestAnimationFrame((t2) => this.frame(t2));
    }
  }

  private finish(): void {
    if (!this.sp) return;
    const { winnerIndex, winnerText } = this.sp;
    this.sp = null;
    this._phase = 'done';
    this.cb.onComplete(winnerIndex, winnerText);
    if (this.oneShot) {
      const cb = this.oneShot;
      this.oneShot = null;
      cb(winnerIndex, winnerText);
    }
  }

  reset(): void {
    if (this.sp) {
      cancelAnimationFrame(this.sp.rafId);
      this.sp = null;
    }
    this._phase = 'idle';
  }
}
